import { onCleanup } from "solid-js"
import { createStore } from "solid-js/store"
import type { SessionTab, useTabs } from "@/context/tabs"
import type { CoworkSessionContext } from "./api"
import type { FollowupDraft } from "@/components/prompt-input/submit"
import { injectCoworkLanguage } from "./language-policy"

export type ReportTemplateRequest = {
  id: string
  templateID: string
  companyID: string
  projectID: string
  periodID: string | null
  sessionID: string
  visiblePrompt: string
  instructions: string
  reportOutputLanguage: string
}

export type ReportTemplateAction = {
  visiblePrompt: string
  instructions: string
  reportOutputLanguage: string
  workflowRunID: string
  expected?: Pick<ReportTemplateRequest, "companyID" | "projectID" | "periodID" | "sessionID">
  request?: ReportTemplateRequest
  status: "pending" | "claimed" | "failed" | "sent" | "queued"
  sending: boolean
  staged?: boolean
  restoreDraft?: boolean
}

export function createReportTemplateAction(
  input: Pick<ReportTemplateAction, "visiblePrompt" | "instructions" | "reportOutputLanguage" | "expected" | "request">,
): ReportTemplateAction {
  return {
    ...input,
    workflowRunID: crypto.randomUUID(),
    status: input.request ? "pending" : "claimed",
    sending: false,
    staged: false,
    restoreDraft: false,
  }
}

export function reportTemplateMemory(tabs: ReturnType<typeof useTabs>, tab: SessionTab) {
  return tabs.state(tab, "report-template-request", () => {
    const [state, setState] = createStore<{ action?: ReportTemplateAction; disposed: boolean }>({ disposed: false })
    onCleanup(() => setState("disposed", true))
    return { state, setState }
  })
}

export function queueReportTemplate(
  tabs: ReturnType<typeof useTabs>,
  tab: SessionTab,
  request: ReportTemplateRequest,
): void {
  const memory = reportTemplateMemory(tabs, tab)
  // A catalog handoff belongs to a fresh tab. Repeated beforeOpen calls cannot replace or replay it.
  if (memory.state.action || memory.state.disposed) return
  memory.setState(
    "action",
    createReportTemplateAction({
      visiblePrompt: request.visiblePrompt,
      instructions: request.instructions,
      reportOutputLanguage: request.reportOutputLanguage,
      expected: { ...request },
      request: { ...request },
    }),
  )
}

export function claimReportTemplate(memory: ReturnType<typeof reportTemplateMemory>) {
  const action = memory.state.action
  if (memory.state.disposed || action?.status !== "pending") return
  memory.setState("action", "status", "claimed")
  return memory.state.action
}

export function matchingReportTemplate(memory: ReturnType<typeof reportTemplateMemory>, draft: FollowupDraft) {
  const action = memory.state.action
  if (!action || action.status === "sent" || action.status === "queued") return
  const text = draft.prompt
    .map((part) => ("content" in part && !("synthetic" in part && part.synthetic) ? part.content : ""))
    .join("")
  return text === action.visiblePrompt ? action : undefined
}

export function advanceReportTemplate(
  memory: ReturnType<typeof reportTemplateMemory>,
  ready: {
    prompt: boolean
    loading: boolean
    model: boolean
    agent: boolean
    session: boolean
    blocked: boolean
    text: string
    expired?: boolean
  },
  callbacks: {
    stage: (text: string) => void
    run: (action: ReportTemplateAction) => void
    fail: (model: boolean) => void
  },
) {
  const action = memory.state.action
  if (memory.state.disposed || !action) return
  if (action.status === "failed" && action.restoreDraft && ready.prompt) {
    memory.setState("action", "restoreDraft", false)
    if (!ready.blocked && !ready.text) {
      memory.setState("action", "staged", true)
      callbacks.stage(action.visiblePrompt)
    }
    return
  }
  if (action.status !== "pending") return
  if (ready.expired) {
    memory.setState("action", { status: "failed", restoreDraft: !ready.prompt && !action.staged })
    callbacks.fail(false)
    return
  }
  if (!ready.prompt || ready.blocked) return
  if (!action.staged && !ready.text) {
    memory.setState("action", "staged", true)
    callbacks.stage(action.visiblePrompt)
  } else if (ready.text !== action.visiblePrompt) {
    claimReportTemplate(memory)
    memory.setState("action", "status", "failed")
    callbacks.fail(false)
    return
  }
  if (ready.loading) return
  const claimed = claimReportTemplate(memory)
  if (!claimed) return
  if (!ready.model || !ready.agent || !ready.session) {
    memory.setState("action", "status", "failed")
    callbacks.fail(!ready.model || !ready.agent)
    return
  }
  callbacks.run(claimed)
}

export function reportTemplateContextMatches(
  context: CoworkSessionContext | undefined,
  remoteSessionID: string,
  expected?: ReportTemplateAction["expected"],
) {
  return (
    !!context &&
    context.remote_opencode_session_id === remoteSessionID &&
    (!expected ||
      (context.company_id === expected.companyID &&
        context.project_id === expected.projectID &&
        (context.period_id ?? context.workflow_id ?? null) === expected.periodID &&
        context.session_id === expected.sessionID))
  )
}

export async function injectReportTemplateContext(input: {
  draft: FollowupDraft
  action?: ReportTemplateAction
  reportOutputLanguage: string
  getContext: (remoteSessionID: string, companyID?: string) => Promise<CoworkSessionContext | undefined>
  manifest: (projectID: string, periodID?: string, companyID?: string, strict?: boolean) => Promise<unknown[]>
  error: () => Error
}) {
  const { draft, action } = input
  const context = await input.getContext(draft.sessionID, action?.expected?.companyID)
  if (action && !reportTemplateContextMatches(context, draft.sessionID, action.expected)) throw input.error()
  if (!context) return draft
  const reportOutputLanguage =
    action?.reportOutputLanguage ?? draft.language?.reportOutputLanguage ?? input.reportOutputLanguage
  return injectCoworkLanguage({
    ...draft,
    executionContext: {
      task: action ? "generate_report" : "chat",
      hints: {
        project_id: context.project_id,
        period_id: context.period_id ?? context.workflow_id ?? undefined,
        report_output_language: reportOutputLanguage,
      },
    },
    ...(draft.language
      ? {
          language: { ...draft.language, reportOutputLanguage },
        }
      : {}),
    prompt: [
      ...(action ? [{ type: "text" as const, content: action.instructions, start: 0, end: action.instructions.length, synthetic: true }] : []),
      ...draft.prompt.filter((part) => !(action && part.type === "text" && part.synthetic && part.content === action.instructions)),
    ],
  })
}
